package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.auth.TokenStorage
import fr.frux.core.common.auth.Tokens
import fr.frux.core.common.auth.UnauthorizedException
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.entitlements.EntitlementsHolder
import fr.frux.core.common.entitlements.ServerClock
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.AuthApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.core.network.error.toAppError
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.data.mapper.toEntitlements
import fr.frux.shared.domain.model.User
import fr.frux.shared.domain.repository.AuthRepository
import fr.frux.shared.domain.repository.DeleteAccountOutcome
import fr.frux.shared.domain.repository.VerifyOutcome
import fr.frux.shared.dto.AccountExportDto
import fr.frux.shared.dto.ErrorResponse
import io.ktor.client.call.body
import io.ktor.client.plugins.ResponseException
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.withContext
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.TimeSource

/**
 * Implementation of AuthRepository.
 * Handles authentication API calls and token management (ADR 0012, passwordless OTP email).
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class AuthRepositoryImpl(
    private val authApi: AuthApi,
    private val tokenStorage: TokenStorage,
    // Écrit à chaque `GET /auth/me` réussi et vidé à toute sortie de session (#454) : la source que
    // `core-ads` (#527) observera, sans dépendre de `:shared`.
    private val entitlements: EntitlementsHolder,
    // Porte la fiche du dernier compte confirmé par le serveur, pour servir l'identité hors ligne (#657).
    private val appPreferences: AppPreferences,
) : AuthRepository {
    override suspend fun requestCode(
        email: String,
        purpose: String?,
    ): Result<String?> =
        runCatchingCancellable {
            authApi.requestCode(email, purpose).devCode
        }.mapFailureToAppError()

    @OptIn(ExperimentalTime::class)
    override suspend fun verifyCode(
        email: String,
        code: String,
        username: String?,
    ): Result<VerifyOutcome> =
        runCatchingCancellable {
            val response = authApi.verifyCode(email, code, username)
            // Nouveau compte, peut-être une autre personne : ni les droits du précédent ni un
            // « sans droit » prématuré, l'état redevient non résolu jusqu'au prochain `/auth/me`.
            entitlements.unresolve()
            tokenStorage.save(
                Tokens(
                    accessToken = response.accessToken,
                    refreshToken = response.refreshToken,
                    accessExpiresAtEpochSeconds = Clock.System.now().epochSeconds + response.expiresIn,
                ),
            )
            val user = response.user.toDomain()
            rememberUser(user)
            VerifyOutcome.Success(user) as VerifyOutcome
        }.recoverKnownVerifyOutcomes()

    /**
     * Compte courant, et droits Frux+ publiés dans [entitlements] (#454, #527).
     *
     * Session anonyme : `clear()` sans garde de génération. Ce n'est sûr que parce que
     * `TokenStorage.load()` (lu par [isAuthenticated]) ne suspend pas réellement : aucune connexion
     * (`verifyCode`, qui fait `unresolve()` puis enregistre les jetons) ne peut s'intercaler entre la
     * lecture « pas de jeton » et le `clear()`. Si un stockage asynchrone le remplaçait, lire
     * `entitlements.generation` avant [isAuthenticated] et publier `NONE` par `updateIf`, sans quoi
     * une connexion concurrente recevrait « sans droit » avant son propre `/auth/me`.
     */
    override suspend fun getCurrentUser(): Result<User?> =
        runCatchingCancellable {
            if (!isAuthenticated()) {
                // Session résolue anonyme : sans droit, plus « non résolu ».
                entitlements.clear()
                null
            } else {
                // Génération lue avant l'appel : une réponse revenue après une déconnexion ou une
                // nouvelle connexion décrit une session finie, elle ne doit rien publier (#454).
                val generation = entitlements.generation
                val dated = authApi.getCurrentUser()
                // Repère monotone pris à la réception, avec l'heure du serveur (#527) : les échéances
                // se décomptent ensuite sans jamais lire l'horloge de l'appareil.
                val serverClock = dated.serverTime?.let { ServerClock(it, TimeSource.Monotonic.markNow()) }
                val current = dated.body
                val premium = current.premium
                if (premium == null) {
                    // API antérieure à #454 : rien n'est su, l'état reste tel quel (non résolu au
                    // besoin) plutôt que « sans droit », qui ouvrirait la publicité à un abonné.
                    Logger.i(TAG, "auth/me without premium state, entitlements left unchanged")
                } else if (!entitlements.updateIf(
                        generation,
                        premium.toEntitlements(
                            serverClock,
                            accountId = current.id,
                            purchaseAccountRef = current.purchaseAccountRef,
                        ),
                    )
                ) {
                    Logger.i(TAG, "Stale auth/me response ignored for entitlements")
                }
                current.toDomain().also { rememberUser(it) }
            }
        }.mapFailureToAppError()

    override suspend fun lastKnownUser(): User? =
        runCatchingCancellable {
            appPreferences
                .getString(
                    LAST_USER_KEY,
                )?.let { lastUserJson.decodeFromString<CachedUserRecord>(it).toDomain() }
        }.getOrNull()

    // Best-effort : la fiche n'est qu'un confort hors ligne, son échec ne doit jamais faire échouer
    // une connexion ou un rafraîchissement qui a réussi côté serveur.
    private suspend fun rememberUser(user: User) {
        runCatchingCancellable {
            appPreferences.putString(LAST_USER_KEY, lastUserJson.encodeToString(CachedUserRecord.from(user)))
        }.onFailure { error -> Logger.e(TAG, "Last known user: write failed", error) }
    }

    private suspend fun forgetUser() {
        runCatchingCancellable { appPreferences.remove(LAST_USER_KEY) }
            .onFailure { error -> Logger.e(TAG, "Last known user: purge failed", error) }
    }

    /**
     * Best-effort côté serveur, purge inconditionnelle côté client (#26, ADR 0019) : l'intention
     * de partir gagne toujours — un réseau indisponible ne doit pas laisser l'appareil connecté
     * localement. La famille de sessions distante, elle, survivra jusqu'à son expiration
     * naturelle si la révocation échoue ; c'est un moindre mal face à un utilisateur bloqué en
     * session après avoir explicitement demandé à en sortir.
     *
     * `finally` réel (#26, C3) : la purge locale doit survivre à une `CancellationException` (le
     * scope qui porte cet appel peut être annulé en plein logout, ex. navigation immédiate) et à
     * une erreur de lecture du stockage — dans les deux cas, laisser un appareil « logout » côté
     * UI mais encore authentifié côté stockage serait pire qu'un logout qui échoue franchement.
     * `NonCancellable` est nécessaire ici : une fois le job annulé, un appel suspendu ordinaire
     * dans `finally` relèverait `CancellationException` avant même de s'exécuter.
     */
    override suspend fun logout(): Result<Unit> =
        try {
            val refreshToken = runCatchingCancellable { tokenStorage.load() }.getOrNull()?.refreshToken
            if (refreshToken != null) {
                runCatchingCancellable { authApi.logout(refreshToken) }
                    .onFailure { error -> Logger.e(TAG, "Logout: server revocation failed", error) }
            }
            Result.success(Unit)
        } finally {
            withContext(NonCancellable) {
                entitlements.clear()
                runCatchingCancellable { tokenStorage.clear() }
                    .onFailure { error -> Logger.e(TAG, "Logout: local purge failed", error) }
                forgetUser()
            }
        }

    override suspend fun isAuthenticated(): Boolean = tokenStorage.load() != null

    /**
     * Ne purge qu'après un succès serveur confirmé (#26, ADR 0019) : contrairement à [logout],
     * « déconnecter tous les appareils » est un geste explicite dont l'utilisateur attend une
     * confirmation — le laisser croire que ses autres appareils sont déconnectés alors que
     * l'appel a échoué serait pire qu'un message d'erreur.
     */
    override suspend fun logoutAll(): Result<Unit> =
        runCatchingCancellable {
            authApi.logoutAll()
            entitlements.clear()
            tokenStorage.clear()
            forgetUser()
        }.mapFailureToAppError()

    override suspend fun deleteAccount(code: String): Result<DeleteAccountOutcome> =
        runCatchingCancellable {
            authApi.deleteAccount(code)
            entitlements.clear()
            DeleteAccountOutcome.Deleted as DeleteAccountOutcome
        }.fold(
            onSuccess = { Result.success(it) },
            onFailure = { error ->
                // #26, ADR 0019 : le code faux/expiré rend désormais 400 `INVALID_CODE`, plus 401 —
                // avec l'auto-refresh client, un 401 métier deviendrait ambigu avec une session
                // réellement morte. Même doctrine que `recoverKnownVerifyOutcomes` ci-dessous.
                if (error.isInvalidCodeBadRequest()) {
                    Result.success(DeleteAccountOutcome.InvalidCode)
                } else {
                    Result.failure(error.toAppError())
                }
            },
        )

    override suspend fun exportAccount(): Result<String> =
        runCatchingCancellable {
            exportJson.encodeToString(AccountExportDto.serializer(), authApi.exportAccount())
        }.mapFailureToAppError()

    private suspend fun Throwable.isInvalidCodeBadRequest(): Boolean =
        this is ResponseException &&
            response.status == HttpStatusCode.BadRequest &&
            runCatching { response.body<ErrorResponse>() }.getOrNull()?.code == "INVALID_CODE"

    /**
     * Reclasse les refus attendus du serveur (code faux, pseudo requis/pris/invalide) en variantes
     * de [VerifyOutcome] plutôt qu'en [Result.failure] : ce sont des issues métier normales de ce
     * formulaire, pas des pannes. Tout le reste (réseau, 5xx, imprévu) suit le mapping habituel.
     *
     * Le 401 ne porte pas le corps `ErrorResponse` : [UnauthorizedException] le remplace déjà en
     * amont (cf. `FruxHttpClient`), et le contrat ne connaît qu'une seule cause de 401 ici —
     * `INVALID_CODE`. Les 400/409, eux, ne sont pas interceptés en amont : leur [ErrorResponse.code]
     * distingue `USERNAME_REQUIRED` de `INVALID_USERNAME`.
     */
    private suspend fun Result<VerifyOutcome>.recoverKnownVerifyOutcomes(): Result<VerifyOutcome> =
        fold(
            onSuccess = { Result.success(it) },
            onFailure = { error ->
                when {
                    error is UnauthorizedException -> {
                        Result.success(VerifyOutcome.InvalidCode)
                    }

                    error is ResponseException && error.response.status == HttpStatusCode.Conflict -> {
                        Result.success(VerifyOutcome.UsernameTaken)
                    }

                    error is ResponseException && error.response.status == HttpStatusCode.BadRequest -> {
                        when (runCatching { error.response.body<ErrorResponse>() }.getOrNull()?.code) {
                            "USERNAME_REQUIRED" -> Result.success(VerifyOutcome.NeedsUsername)
                            "INVALID_USERNAME" -> Result.success(VerifyOutcome.UsernameInvalid)
                            else -> Result.failure(error.toAppError())
                        }
                    }

                    else -> {
                        Result.failure(error.toAppError())
                    }
                }
            },
        )

    private companion object {
        const val TAG = "AuthRepositoryImpl"
        const val LAST_USER_KEY = "session_last_user"
    }
}

/** JSON lisible : c'est un export destiné à être ouvert par la personne elle-même, pas rejoué. */
private val exportJson = Json { prettyPrint = true }

/** Tolérant aux champs inconnus : une fiche écrite par une version plus récente reste lisible. */
private val lastUserJson = Json { ignoreUnknownKeys = true }

/** Fiche locale du dernier compte confirmé par le serveur, distincte du DTO réseau pour évoluer seule. */
@Serializable
private data class CachedUserRecord(
    val id: Long,
    val username: String,
    val email: String,
    val createdAt: Long,
) {
    fun toDomain() = User(id = id, username = username, email = email, createdAt = createdAt)

    companion object {
        fun from(user: User) = CachedUserRecord(user.id, user.username, user.email, user.createdAt)
    }
}
